import React, { useEffect, useState } from 'react';
import CtaCard from 'components/CtaCard';
import Call from 'components/Icons/Call';
import Cross from 'components/Icons/Cross';
import Gmail from 'components/Icons/Gmail';
import Instagram from 'components/Icons/Instagram';
import Messenger from 'components/Icons/Messenger';
import Whatsapp from 'components/Icons/Whatsapp';
import ReactModal from 'react-modal';
import 'utilities/global.css';
import PreviewStyle from './preview.module.css';
import PreviewMobileStyle from './previewMobile.module.css';
import CAStyles from './style.module.css';
import SVGFactory, { SVGName } from 'widgets/STW/components/svgFactory';
import { decodeTextHelper } from '../utility';
import {
  IRedirectWidgetCustomisation,
  WidgetSchema,
  LANGUAGE_VALUES,
  LANGUAGE_ISO_CODES,
} from '@bikdotai/bik-models/dm';
import { LanguageUtils } from 'utilities/languageUtilities';

export interface IBottomDrawerModal extends WidgetSchema {
  id?: string;
  preview?: boolean;
  isMobile?: boolean;
  showModal: boolean;
  environment: string;
  onClose: () => void;
}

const BottomDrawerModal = (props: IBottomDrawerModal) => {
  const languageUtils = new LanguageUtils();
  const [widgetLanguage, setWidgetLanguage] = useState(LANGUAGE_VALUES.ENGLISH);
  let currentStyle;
  const { environment, widgetCustomisation } = props as unknown as {
    environment: string;
    widgetCustomisation: IRedirectWidgetCustomisation;
  };
  const redirectWidgetBackgroundColor =
    widgetCustomisation?.redirectWidgetBackgroundColor ?? '#e0e0e0';
  const bannerImage =
    widgetCustomisation?.redirectWidgetBannerImage ??
    'https://firebasestorage.googleapis.com/v0/b/bikayi-chat.appspot.com/o/images-uid%2Fbc3c06b3-e5cd-4d01-bd18-4a8e3639a665?alt=media&token=7e7b6987-c34c-4043-9b6e-3842b94d023d';
  const title =
    widgetCustomisation?.multiLingualConfigurations?.[widgetLanguage]
      ?.redirectWidgetTitleText ??
    widgetCustomisation?.redirectWidgetTitleText ??
    'Hi there';
  let inlineOverlayStyle;
  const { top, bottom, right, left } = widgetCustomisation?.fabPositioner ?? {
    bottom: 30,
    right: 30,
  };
  if (props?.preview) {
    if (props?.isMobile) {
      currentStyle = PreviewMobileStyle;
    } else {
      currentStyle = PreviewStyle;
      inlineOverlayStyle = {
        position: 'absolute',
      };
    }
  } else {
    currentStyle = CAStyles;
    inlineOverlayStyle = {
      zIndex: 2147483647,
      pointerEvents: props?.preview ? 'none' : 'inherit',
      top: 'inherit',
      bottom: screen.width > 768 ? '30px' : '0px',
      left: screen.width > 768 ? (left ? '30px' : 'inherit') : '0px',
      right: screen.width > 768 ? (right ? '30px' : 'inherit') : '0px',
      transform: screen.width > 768 ? 'inherit' : 'inherit',
    };
  }
  const [show, setShow] = useState(false);
  const {
    whatsapp,
    messenger,
    phoneNumber,
    instagram,
    email,
    emailSubject,
    emailBody,
  } = widgetCustomisation as IRedirectWidgetCustomisation;
  let parent;

  if (props.preview) {
    parent = document.getElementById('preview-container');
  } else {
    parent = document.body;
  }

  useEffect(() => {
    setShow(props.showModal);
  }, [props.showModal]);

  useEffect(() => {
    const isBikURL =
      window.location.href?.includes('https://dashboard.bik.ai/') ||
      window.location.href?.includes('https://staging.dashboard.bik.ai/');
    const previewLanguage = props?.widgetCustomisation?.previewLanguage;
    const storeLanguage =
      (isBikURL ? previewLanguage : window?.Shopify?.locale) ??
      previewLanguage ??
      LANGUAGE_ISO_CODES.EN;
    setWidgetLanguage(languageUtils.getStoreLanguage(storeLanguage));
  }, [window.location.href, props?.widgetCustomisation?.previewLanguage]);

  return (
    <>
      <ReactModal
        isOpen={show}
        ariaHideApp={false}
        parentSelector={() => parent}
        style={{
          overlay: inlineOverlayStyle,
        }}
        className={currentStyle.content}
        overlayClassName={currentStyle.overlay}
        shouldCloseOnOverlayClick={true}
      >
        <div>
          <button
            className={currentStyle.crossBtn}
            style={{ backgroundColor: redirectWidgetBackgroundColor }}
            onClick={props.onClose}
          >
            <Cross
              color={'#5C5C5C'}
              height={props.preview ? 10 : 24}
              width={props.preview ? 10 : 24}
            />
          </button>
          <div className={currentStyle.widgetContainer}>
            <div className={currentStyle.widgetBody}>
              <div className={currentStyle.widgetHeader}>
                <img className={currentStyle.bannerImage} src={bannerImage} />
              </div>
              <div className={currentStyle.widgetContent}>
                <div className={currentStyle.title}>{title}</div>
                <div className={currentStyle.widgetRedirects}>
                  {whatsapp && (
                    <CtaCard
                      id={`bik-bd-${props.id}-cta-whatsapp`}
                      isMobile={props.isMobile}
                      Icon={() => (
                        <Whatsapp
                          height={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                          width={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                        />
                      )}
                      text={'Open Whatsapp'}
                      onClick={() => {
                        const pathName = location.href.replace(
                          location.origin,
                          '',
                        );
                        window.open(
                          `${environment}/publicApiFunctions-redirectToChat/?contextType=bik-widgets&contextId=${props.id}&pathname=${pathName}&sourceUrl=${location.href}`,
                        );
                      }}
                      preview={props.preview}
                    />
                  )}
                  {messenger && (
                    <CtaCard
                      id={`bik-bd-${props.id}-cta-messenger`}
                      isMobile={props.isMobile}
                      Icon={() => (
                        <Messenger
                          height={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                          width={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                        />
                      )}
                      text={'Open Facebook Messenger'}
                      onClick={() => {
                        window.open(`https://m.me/${messenger}`);
                      }}
                      preview={props.preview}
                    />
                  )}
                  {instagram && (
                    <CtaCard
                      id={`bik-bd-${props.id}-cta-instagram`}
                      isMobile={props.isMobile}
                      text={'Open Instagram'}
                      Icon={() => (
                        <Instagram
                          height={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                          width={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                        />
                      )}
                      onClick={() => {
                        window.open(`https://www.instagram.com/${instagram}`);
                      }}
                      preview={props.preview}
                    />
                  )}
                  {email && (
                    <CtaCard
                      id={`bik-bd-${props.id}-cta-email`}
                      isMobile={props.isMobile}
                      Icon={() => (
                        <Gmail
                          height={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                          width={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                        />
                      )}
                      text={'Open Gmail'}
                      onClick={() => {
                        window.open(
                          `https://mail.google.com/mail/u/0/?fs=1&to=${email}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(decodeTextHelper(emailBody))}&tf=cm`,
                        );
                      }}
                      preview={props.preview}
                    />
                  )}
                  {phoneNumber && (
                    <CtaCard
                      id={`bik-bd-${props.id}-cta-phone`}
                      isMobile={props.isMobile}
                      Icon={() => (
                        <Call
                          height={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                          width={
                            props.preview ? (props.isMobile ? 20 : 10) : 24
                          }
                        />
                      )}
                      text={`Call ${phoneNumber}`}
                      onClick={() => {
                        window.open(`tel:${phoneNumber}`);
                      }}
                      preview={props.preview}
                    />
                  )}
                  <div
                    style={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    <button
                      onClick={() =>
                        window.open(
                          'https://bik.ai/?utm_campaign=Bottom_Drawer&utm_medium=Widgets&utm_source=Powered_by_bik',
                        )
                      }
                      className={currentStyle.poweredByBik}
                    >
                      {languageUtils.getPoweredByMessage(widgetLanguage)}
                      <SVGFactory name={SVGName.BIK} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default BottomDrawerModal;
